/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useCallback } from 'react'
import { Box, Grid } from '@mui/material'
import {
  required,
  TextInput,
  SelectInput,
  FormTab,
  maxLength,
  useTranslate,
  TransformData,
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { FormBottomToolbarWithPermission } from '../../components/form'
import { CreateBase } from '../../components/base'
import { COMMON_FIELDS, NEWS_FIELDS, SERVICES_FIELDS } from '../../providers/localization/constant'
import { PeriodDateInputs } from '../../components/domains/services'
import { ImageInput } from '../../components/inputs/ImageInput'
import { TabbedMultiCardForm } from '../../components/tab'
import ApplyToEnglishButton from '../../components/buttons/ApplyToEnglishButton'
import { ServicesSelectInput } from '../../components/form/ServicesSelectInput'
import { MyRichTextInput } from '../../components/text-editor'
import { ThumbnailInput } from '../../components/inputs/ThumbnailInput'
import { isYoutubeLink } from '../../utils/validators'
import sanitizeHtml from 'sanitize-html'
import trim from 'lodash/trim'
import unescape from 'lodash/unescape'

const RESOURCE_PREFIX = 'resources.services.create'

const PersistForm: React.FC = () => {
  const [isPromotion, setPromotion] = useState(null)
  const { unregister } = useFormContext()
  useEffect(() => {
    if (!isPromotion) {
      unregister('start_date')
      unregister('end_date')
    }
  }, [isPromotion, unregister])
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <SelectInput
          source='type'
          label={`${SERVICES_FIELDS}.type.label`}
          choices={[
            { id: 'n', name: `${SERVICES_FIELDS}.type.choices.normal` },
            { id: 'p', name: `${SERVICES_FIELDS}.type.choices.promotion` },
          ]}
          onChange={(e): void => {
            setPromotion(e.target.value === 'p')
          }}
          sx={{ margin: 0 }}
          fullWidth
          validate={[required()]}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        {isPromotion ? <PeriodDateInputs /> : <div style={{ width: '100%' }}></div>}
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput
          source='youtube_link'
          label={`${SERVICES_FIELDS}.youtube_link`}
          validate={[isYoutubeLink()]}
          fullWidth
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          source='slug_th'
          label={`${COMMON_FIELDS}.slug_th.label`}
          InputProps={{
            fullWidth: true,
          }}
          fullWidth
          validate={[required()]}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          source='slug_en'
          label={`${COMMON_FIELDS}.slug_en.label`}
          InputProps={{
            fullWidth: true,
          }}
          fullWidth
          validate={[required()]}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ServicesSelectInput
          source='booking_service_key'
          label={`${SERVICES_FIELDS}.booking_service_key`}
          fullWidth
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextInput
          source='green_card_description'
          label={`${SERVICES_FIELDS}.green_card_description`}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput
          source='red_card_description'
          label={`${SERVICES_FIELDS}.red_card_description`}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput
          source='ev_card_description'
          label={`${SERVICES_FIELDS}.ev_card_description`}
          fullWidth
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <ImageInput
          source='images'
          label={`${SERVICES_FIELDS}.images`}
          accept='image/*'
          maxSize={10000000}
          multiple
          fallbackSource='full_path'
          helperText={`${COMMON_FIELDS}.image.helper`}
          deleteFileField='delete_images'
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ThumbnailInput
          source='thumbnail'
          label={`${SERVICES_FIELDS}.thumbnail`}
          accept='image/*'
          maxSize={10000000}
          multiple
          fallbackSource='full_path'
          helperText={`${COMMON_FIELDS}.image.helper`}
          deleteFileField='delete_images'
        />
      </Grid>
    </Grid>
  )
}

const ServiceCreate: React.FC = () => {
  const translate = useTranslate()
  const [editorContentEnKey, setEditorContentEnKey] = useState(0)
  const reloadTextEditor = (): void => {
    setEditorContentEnKey(editorContentEnKey + 1)
  }
  const transform: TransformData = useCallback((data) => {
    return {
      ...data,
      title_th: sanitizeHtml(unescape(data.title_th)),
      title_en: sanitizeHtml(unescape(data.title_en)),
      short_description_th: sanitizeHtml(unescape(data.short_description_th)),
      short_description_en: sanitizeHtml(unescape(data.short_description_en)),
      description_th: sanitizeHtml(unescape(data.description_th)),
      description_en: sanitizeHtml(unescape(data.description_en)),
      slug_th: trim(data.slug_th),
      slug_en: trim(data.slug_en),
    }
  }, [])
  return (
    <CreateBase
      title={`${RESOURCE_PREFIX}.title`}
      listAction={{
        hasBackButton: true,
      }}
      noCard
      transform={transform}
    >
      <TabbedMultiCardForm
        childrenForms={[<PersistForm key='services-form' />]}
        formProps={{
          toolbar: null,
          toolbarBottom: (
            <FormBottomToolbarWithPermission
              hasConfirm={true}
              isCreate={true}
              encType='multipart/form-data'
            />
          ),
        }}
      >
        <FormTab label='TH'>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInput
                source='title_th'
                label={`${SERVICES_FIELDS}.title_th`}
                InputProps={{
                  fullWidth: true,
                }}
                fullWidth
                validate={[
                  required(),
                  maxLength(
                    50,
                    `${translate(`${SERVICES_FIELDS}.title_th`)} ต้องมีความยาวไม่เกิน 50 ตัวอักษร`,
                  ),
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source='short_description_th'
                label={`${SERVICES_FIELDS}.short_description_th`}
                InputProps={{
                  fullWidth: true,
                }}
                multiline
                rows={3}
                fullWidth
                validate={[
                  required(),
                  maxLength(
                    150,
                    `${translate(
                      `${SERVICES_FIELDS}.short_description_th`,
                    )} ต้องมีความยาวไม่เกิน 150 ตัวอักษร`,
                  ),
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <MyRichTextInput
                source='description_th'
                label={`${SERVICES_FIELDS}.description_th`}
                component={Box}
                fullWidth
                validate={[required()]}
              />
            </Grid>
            <ApplyToEnglishButton updateKeyEditor={reloadTextEditor} />
          </Grid>
        </FormTab>
        <FormTab label='EN'>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInput
                source='title_en'
                label={`${SERVICES_FIELDS}.title_en`}
                InputProps={{
                  fullWidth: true,
                }}
                fullWidth
                validate={[
                  required(),
                  maxLength(
                    50,
                    `${translate(`${SERVICES_FIELDS}.title_en`)} ต้องมีความยาวไม่เกิน 50 ตัวอักษร`,
                  ),
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source='short_description_en'
                label={`${SERVICES_FIELDS}.short_description_en`}
                InputProps={{
                  fullWidth: true,
                }}
                multiline
                rows={3}
                fullWidth
                validate={[
                  required(),
                  maxLength(
                    150,
                    `${translate(
                      `${SERVICES_FIELDS}.short_description_en`,
                    )} ต้องมีความยาวไม่เกิน 150 ตัวอักษร`,
                  ),
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <MyRichTextInput
                source='description_en'
                label={`${SERVICES_FIELDS}.description_en`}
                component={Box}
                fullWidth
                validate={[required()]}
              />
            </Grid>
            <ApplyToEnglishButton updateKeyEditor={reloadTextEditor} />
          </Grid>
        </FormTab>
      </TabbedMultiCardForm>
    </CreateBase>
  )
}

export default ServiceCreate
